import { updateQuery } from '../helpers/url';
import { useQueryParam, useRouter } from './routing';

export interface UseParamDrivenValueOptions {
  /**
   * The query parameter name to use (defaults to 'tab')
   */
  param?: string;
  /**
   * Array of valid values. If the URL param value is not in this array, it will be ignored.
   * If not provided, any value from the URL will be accepted.
   */
  validValues?: string[];
  /**
   * Default value to use if no valid query param is present.
   * Required if validValues is not provided.
   */
  defaultValue?: string;
}

/**
 * Hook that syncs a value with URL query parameters.
 * Useful for tabs, chip navigation, and other UI elements that should be shareable via URL.
 *
 * @example
 * ```tsx
 * // With validation
 * const { value, setValue } = useParamDrivenValue({
 *   param: 'view',
 *   validValues: ['list', 'grid', 'calendar'],
 *   defaultValue: 'list',
 * });
 *
 * // Without validation (accepts any value)
 * const { value, setValue } = useParamDrivenValue({
 *   param: 'filter',
 *   defaultValue: 'all',
 * });
 * ```
 */
export function useParamDrivenValue(options: UseParamDrivenValueOptions): {
  value: string;
  setValue: (newValue: string) => void;
} {
  const { param = 'tab', validValues, defaultValue } = options;
  const { history } = useRouter();
  const queryValue = useQueryParam(param);

  // Determine the active value
  let value: string;
  if (validValues) {
    // With validation: use query param if valid, otherwise use default or first valid value
    value =
      queryValue && validValues.includes(queryValue) ? queryValue : defaultValue || validValues[0];
  } else {
    // Without validation: use query param if present, otherwise use default
    value = queryValue || defaultValue || '';
  }

  const setValue = (newValue: string) => {
    if (validValues && !validValues.includes(newValue)) {
      console.warn(
        `useParamDrivenValue: Attempted to set invalid value "${newValue}". Valid values are: ${validValues.join(', ')}`,
      );
      return;
    }

    updateQuery(history, {
      [param]: newValue,
    });
  };

  return { value, setValue };
}
